import { useState, useEffect, useMemo, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
    CalendarClock, RefreshCw, Search, ChevronDown, AlertTriangle, Clipboard,
    Check, X, Send, CalendarX, HelpCircle, Pencil,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { DEMO_CONFIRMATIONS_URL, DEMO_CONFIRMATION_UPDATE, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";

const BUCKETS = [
    {
        key: "rescue", label: "Needs rescue", icon: CalendarX,
        blurb: "The demo date passed and the stage never moved. These are being lost silently.",
        tone: "border-rose-300 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/15",
        head: "text-rose-800 dark:text-rose-200",
    },
    {
        key: "upcoming", label: "Coming up", icon: CalendarClock,
        blurb: "Confirm these before the slot so they don't become no-shows.",
        tone: "border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/15",
        head: "text-indigo-900 dark:text-indigo-200",
    },
    {
        key: "missing_date", label: "No date recorded", icon: HelpCircle,
        blurb: "Marked Demo Fixed but nobody wrote down when. Nothing can be confirmed.",
        tone: "border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/15",
        head: "text-amber-900 dark:text-amber-200",
    },
];

const STATUS_TABS = [
    { value: "draft",   label: "To action" },
    { value: "sent",    label: "Sent" },
    { value: "skipped", label: "Skipped" },
    { value: "all",     label: "All" },
];

const riskTone = (r) =>
    r >= 70 ? "bg-rose-500" : r >= 45 ? "bg-amber-500" : "bg-gray-400";
const riskLabel = (r) => (r >= 70 ? "High risk" : r >= 45 ? "Watch" : "Low risk");

const DemoDesk = () => {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();

    const [rows,    setRows]    = useState([]);
    const [counts,  setCounts]  = useState(null);
    const [loading, setLoading] = useState(false);
    const [search,  setSearch]  = useState("");
    const [statusTab,      setStatusTab]      = usePersistedState("demo_desk_status", "draft");
    const [selectedUserId, setSelectedUserId] = usePersistedState("demo_desk_user", "");
    const [users,   setUsers]   = useState([]);
    const [savingId, setSavingId] = useState(null);
    const [editId,  setEditId]  = useState(null);
    const [draftText, setDraftText] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (statusTab)      params.append("status",  statusTab);
        if (selectedUserId) params.append("user_id", selectedUserId);
        const qs = params.toString();
        fetch(qs ? `${DEMO_CONFIRMATIONS_URL}?${qs}` : DEMO_CONFIRMATIONS_URL)
            .then(res => {
                if (res?.status === "success") {
                    setRows(Array.isArray(res.data) ? res.data : []);
                    setCounts(res.counts || null);
                } else {
                    ToastMsg("error", res?.message || "Failed to load the demo desk");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load the demo desk"))
            .finally(() => setLoading(false));
    }, [statusTab, selectedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        if (!search.trim()) return rows;
        const q = search.trim().toLowerCase();
        return rows.filter(r =>
            r.customer_name?.toLowerCase().includes(q) ||
            r.contact_number?.toLowerCase().includes(q) ||
            r.user_name?.toLowerCase().includes(q));
    }, [rows, search]);

    const grouped = useMemo(() => {
        const out = { rescue: [], upcoming: [], missing_date: [] };
        filtered.forEach(r => {
            if (r.stage === "rescue") out.rescue.push(r);
            else if (r.stage === "missing_date") out.missing_date.push(r);
            else out.upcoming.push(r);
        });
        return out;
    }, [filtered]);

    const setStatus = (row, status, messageText) => {
        setSavingId(row.id);
        const body = { status };
        if (messageText !== undefined) body.message_text = messageText;
        update(DEMO_CONFIRMATION_UPDATE(row.id), body)
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", status === "sent" ? "Marked as sent" : `Marked ${status}`);
                    setEditId(null);
                    setRows(prev => statusTab === "all"
                        ? prev.map(r => (r.id === row.id ? { ...r, status } : r))
                        : prev.filter(r => r.id !== row.id));
                } else {
                    ToastMsg("error", res?.message || "Could not update");
                }
            })
            .catch(() => ToastMsg("error", "Could not update"))
            .finally(() => setSavingId(null));
    };

    const copyAndOpen = (row) => {
        const text = row.message_text || "";
        navigator.clipboard?.writeText(text).catch(() => {});
        const number = (row.contact_number || "").replace(/[^\d]/g, "");
        if (number) {
            // Opens the rep's own WhatsApp with the text prefilled — a person
            // still presses send, so the CRM never messages a hospital by itself.
            const wa = number.length === 10 ? `91${number}` : number;
            window.open(`https://wa.me/${wa}?text=${encodeURIComponent(text)}`, "_blank");
        } else {
            ToastMsg("success", "Message copied — no phone number on this lead");
        }
    };

    const Card = ({ row }) => {
        const isEditing = editId === row.id;
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                <div className="flex">
                    <div className={cn("w-1 flex-shrink-0", riskTone(row.risk_score))} />
                    <div className="flex-1 min-w-0 p-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                                <button
                                    onClick={() => navigate("/customers/customerdetails", {
                                        state: { formEdit: true, row: { id: row.customer_id } },
                                    })}
                                    className="text-sm font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 truncate max-w-[260px] text-left">
                                    {row.customer_name || "—"}
                                </button>
                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
                                    {row.demo_datetime
                                        ? <span className="font-medium">
                                            {dayjs(row.demo_datetime).format("ddd DD MMM, hh:mm A")}
                                          </span>
                                        : <span className="text-amber-600 dark:text-amber-400">no date</span>}
                                    {row.contact_number && <><span>·</span><span>{row.contact_number}</span></>}
                                    <span>·</span><span>{row.user_name || "Unassigned"}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                                    {riskLabel(row.risk_score)} · {row.risk_score}
                                </span>
                                {row.demo_source === "followup_date" && (
                                    <span title="No demo date field was filled — this uses the follow-up date on the booking note"
                                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                        inferred date
                                    </span>
                                )}
                            </div>
                        </div>

                        {(row.risk_reasons || []).length > 0 && (
                            <ul className="mt-2 space-y-0.5">
                                {row.risk_reasons.slice(0, 3).map((why, i) => (
                                    <li key={i} className="text-[11px] text-gray-600 dark:text-gray-300 flex gap-1.5">
                                        <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0 mt-0.5" />
                                        <span>{why}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {row.message_text ? (
                            <div className="mt-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 p-2.5">
                                {isEditing ? (
                                    <textarea rows={3} value={draftText}
                                        onChange={e => setDraftText(e.target.value)}
                                        className="w-full text-xs bg-transparent outline-none resize-none text-gray-800 dark:text-gray-100" />
                                ) : (
                                    <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-line">
                                        {row.message_text}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500 italic">
                                No message drafted — run the worker without --no-ai to generate one.
                            </p>
                        )}

                        <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                            {row.status === "draft" ? (
                                <>
                                    {row.message_text && !isEditing && (
                                        <button disabled={savingId === row.id}
                                            onClick={() => copyAndOpen(row)}
                                            className="h-7 px-2.5 rounded-md text-[11px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                                            <Send className="h-3 w-3" /> Copy &amp; open WhatsApp
                                        </button>
                                    )}
                                    {isEditing ? (
                                        <>
                                            <button disabled={savingId === row.id}
                                                onClick={() => setStatus(row, "sent", draftText)}
                                                className="h-7 px-2.5 rounded-md text-[11px] font-medium bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center gap-1">
                                                <Check className="h-3 w-3" /> Save &amp; mark sent
                                            </button>
                                            <button onClick={() => setEditId(null)}
                                                className="h-7 w-7 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button disabled={savingId === row.id}
                                                onClick={() => setStatus(row, "sent")}
                                                className="h-7 px-2.5 rounded-md text-[11px] font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1">
                                                <Check className="h-3 w-3" /> Mark sent
                                            </button>
                                            {row.message_text && (
                                                <button onClick={() => { setEditId(row.id); setDraftText(row.message_text); }}
                                                    className="h-7 px-2.5 rounded-md text-[11px] border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-1">
                                                    <Pencil className="h-3 w-3" /> Edit
                                                </button>
                                            )}
                                            <button disabled={savingId === row.id}
                                                onClick={() => setStatus(row, "skipped")}
                                                className="h-7 px-2.5 rounded-md text-[11px] border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1">
                                                <X className="h-3 w-3" /> Skip
                                            </button>
                                        </>
                                    )}
                                    {row.message_text && (
                                        <button onClick={() => {
                                            navigator.clipboard?.writeText(row.message_text);
                                            ToastMsg("success", "Message copied");
                                        }}
                                            title="Copy only"
                                            className="h-7 w-7 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center">
                                            <Clipboard className="h-3 w-3" />
                                        </button>
                                    )}
                                </>
                            ) : (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                    {row.status}{row.sent_at ? ` · ${dayjs(row.sent_at).format("DD MMM hh:mm A")}` : ""}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <ContainerCard>
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-4 px-1 gap-3 flex-wrap">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <CalendarClock className="h-4 w-4 text-indigo-500" />
                            Demo Desk
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Every booked demo, ranked by how likely it is to not happen — with a message ready to send
                        </p>
                    </div>
                    <Button onClick={load} size="sm" variant="outline"
                        className="flex items-center gap-1 text-xs" disabled={loading}>
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                    </Button>
                </div>

                {counts && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        {[
                            { label: "On the desk", value: counts.total },
                            { label: "High risk",   value: counts.at_risk, tone: "text-rose-600 dark:text-rose-400" },
                            { label: "Needs rescue", value: counts.rescue, tone: "text-rose-600 dark:text-rose-400" },
                            { label: "No date",     value: counts.missing_date, tone: "text-amber-600 dark:text-amber-400" },
                        ].map(c => (
                            <div key={c.label} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{c.label}</p>
                                <p className={cn("text-sm font-semibold tabular-nums", c.tone || "text-gray-900 dark:text-white")}>
                                    {c.value}
                                </p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
                        {STATUS_TABS.map(t => (
                            <button key={t.value} onClick={() => setStatusTab(t.value)}
                                className={cn("px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                                    statusTab === t.value
                                        ? "bg-indigo-600 text-white"
                                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700")}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="relative">
                        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none cursor-pointer">
                            <option value="">All Reps</option>
                            {users.map(u => {
                                const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                                return <option key={u.id} value={u.id}>{name}</option>;
                            })}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                        <Search className="h-3 w-3 text-gray-400" />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Hospital, number or rep…"
                            className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-40" />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading demo desk…</p>
                    </div>
                </div>
            ) : !filtered.length ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-6">
                        <CalendarClock className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Nothing on the desk</p>
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            {rows.length === 0
                                ? "Build the worklist with: manage.py prepare_demo_confirmations --tenant <slug>"
                                : "Nothing matches the current filters."}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 overflow-auto space-y-5 pr-0.5">
                    {BUCKETS.map(bucket => {
                        const items = grouped[bucket.key];
                        if (!items.length) return null;
                        const Icon = bucket.icon;
                        return (
                            <div key={bucket.key}>
                                <div className={cn("rounded-lg border px-3 py-2 mb-2", bucket.tone)}>
                                    <p className={cn("text-xs font-semibold flex items-center gap-1.5", bucket.head)}>
                                        <Icon className="h-3.5 w-3.5" />
                                        {bucket.label} · {items.length}
                                    </p>
                                    <p className="text-[11px] mt-0.5 text-gray-600 dark:text-gray-300">{bucket.blurb}</p>
                                </div>
                                <div className="space-y-2">
                                    {items.map(row => <Card key={row.id} row={row} />)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </ContainerCard>
    );
};

export default DemoDesk;
